/** Record-changing actions (plan D4, §3) — one place that queues them in the draft and
 *  runs the one-step "… now", shared by every control that offers one: the action
 *  fields' controls in the panels (`ActionControl`) and the header's Actions menu
 *  (`useRecordActionItems`). Mounted by `RecordEditorProvider` around the page.
 *
 *  - **Queue**: one SET on the action's draft path (`action:<key>`; cancel travels as its
 *    booking_state field) from the stored baseline — one undo step, a ledger row like any
 *    change. From view mode (a draft kept in the editor) it opens the editor. A
 *    transition with a catalog `confirm` asks first, one line on the control.
 *  - **"… now"** (clean draft only): the body is exactly that one change, compiled like the
 *    draft's. It is previewed first; the save asks — a one-line confirmation on the
 *    control — exactly when Save would (guest-visible events, external writes, a
 *    cancellation), and also when the transition has a `confirm`, the change carries a
 *    warning, or a question needs its answer (a cancellation's mode, default from the
 *    catalog). Then it applies. The receipt goes to the ledger in edit mode and stays on
 *    the control in view mode. The record re-seeds from the response (the apply hook).
 *  - **Pickers** (`widget`, e.g. change owner's user picker) open in a popover on their
 *    trigger; they queue or run "… now" through the same two calls.
 *  - Availability is the record kind's (`config.actionAvailable`): no permission → no
 *    control at all.
 */
import { Alert, Box, Button, LinearProgress, Popover, Typography } from '@mui/material';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { initialDraftState, recordKey, type RecordKey } from '../draft/changeSet';
import { useDraft, useDraftSummary } from '../draft/DraftProvider';
import {
  bindAnswer,
  choiceExtras,
  confirmedValues,
  confirmersOf,
  optionDefault,
  raisedChoices,
  UPDATE_EVENTS_OPTION,
  type ChoiceAnswers,
} from '../editor/choices';
import { asDriftFailure, asValidationFailure, failureMessage, type ValidationFailure } from '../editor/errors';
import { useRecordEditor } from '../editor/RecordEditorProvider';
import type { RecordAction, RecordApplyResult, RecordPreview } from '../editor/types';
import { ChoiceQuestion } from '../fields/ChoiceQuestion';
import { LedgerReceipt } from '../ledger/LedgerReceipt';
import { confirmItems } from '../ledger/model';

import { actionDraftPath, actionModes, offeredTransitions, oneStepChangeset, type ActionModes } from './model';

export interface ActionRequest {
  action: RecordAction;
  rec: RecordKey;
  to: unknown;
  /** What the operator picked, in words ("Check in", "Change owner to Jane Roe"). */
  label: string;
  /** Whose value it changes when that is not the opened record ("Tom Doe"). */
  subject?: string;
  /** Where a confirmation, a "… now" or its receipt hangs. */
  anchorEl: HTMLElement | null;
  /** The transition's one-line confirmation (catalog `confirm`). */
  confirm?: string | null;
  /** Focus the field once queued (an action started away from its field — the header). */
  focusField?: boolean;
}

type OneStepPhase = 'checking' | 'confirm' | 'saving' | 'failed' | 'done';

interface OneStep {
  req: ActionRequest;
  phase: OneStepPhase;
  error: string | null;
  result: RecordApplyResult | null;
}

export interface RecordActionsValue {
  /** The action can be offered on `rec` at all (the record kind's availability). */
  isAvailable: (action: RecordAction, rec: RecordKey) => boolean;
  /** How controls offer `action` right now (queue · "… now"). */
  modes: (action: RecordAction) => ActionModes;
  /** The action field's value on `rec`: the draft's in edit mode, the stored one in view. */
  valueOf: (action: RecordAction, rec: RecordKey) => unknown;
  queue: (req: ActionRequest) => void;
  runNow: (req: ActionRequest) => void;
  openWidget: (action: RecordAction, rec: RecordKey, anchorEl: HTMLElement) => void;
  /** A "… now" is in flight (its controls are disabled meanwhile). */
  busy: boolean;
}

const Ctx = createContext<RecordActionsValue | null>(null);

/** A refused one-change body: its change's own error says most. */
function refusalMessage(v: ValidationFailure): string {
  return Object.values(v.errors).flat()[0]?.message ?? v.message;
}

const POPOVER_PAPER_SX = { p: 1.5, mt: 0.5, width: 340, maxWidth: 'calc(100vw - 32px)', display: 'grid', gap: 1 } as const;

function Kbd({ children }: { children: ReactNode }) {
  return (
    <Box
      component="kbd"
      sx={{ ml: 0.75, px: 0.5, border: 1, borderColor: 'currentColor', borderRadius: 0.5, fontFamily: 'inherit', fontSize: 11, fontWeight: 600, lineHeight: 1.4, opacity: 0.75 }}
    >
      {children}
    </Box>
  );
}

export function RecordActionsProvider({
  enabled = true,
  onEnterEdit,
  children,
}: {
  /** The page's own gate (the operator's permissions, the kill switch): false = no
   *  action is offered anywhere. Default true — the record kind's availability decides. */
  enabled?: boolean;
  /** Open the editor (view mode → queue). Absent: queueing from view mode is not offered. */
  onEnterEdit?: (() => void) | undefined;
  children: ReactNode;
}) {
  const editor = useRecordEditor();
  const { mode, config, focusField, setReceipt } = editor;
  const { state, dispatch, baselineOf } = useDraft();
  const { count } = useDraftSummary();
  const editing = mode === 'edit';
  const dirty = count > 0;
  const canEdit = Boolean(onEnterEdit);

  const { actionAvailable } = config;
  const isAvailable = useCallback(
    (action: RecordAction, rec: RecordKey) =>
      enabled && action.available !== false && (actionAvailable?.(action, rec) ?? true),
    [enabled, actionAvailable],
  );
  const modes = useCallback(
    (action: RecordAction) => actionModes({ action, editing, dirty, canEdit }),
    [editing, dirty, canEdit],
  );
  const valueOf = useCallback(
    (action: RecordAction, rec: RecordKey) => {
      const path = actionDraftPath(action);
      const entry = editing ? state.entries[rec]?.[path] : undefined;
      return entry ? entry.to : baselineOf(rec, path);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editing, state.version, baselineOf],
  );

  // ---- queue ----------------------------------------------------------------
  const [pendingQueue, setPendingQueue] = useState<ActionRequest | null>(null);
  const queueNow = useCallback(
    (req: ActionRequest) => {
      const path = actionDraftPath(req.action);
      dispatch({ type: 'SET', changes: [{ rec: req.rec, path, from: baselineOf(req.rec, path), to: req.to }] });
      if (!editing) onEnterEdit?.();
      if (req.focusField) requestAnimationFrame(() => focusField(req.rec, path));
    },
    [dispatch, baselineOf, editing, onEnterEdit, focusField],
  );
  const queue = useCallback(
    (req: ActionRequest) => (req.confirm ? setPendingQueue(req) : queueNow(req)),
    [queueNow],
  );

  // ---- one step ---------------------------------------------------------------
  const [one, setOne] = useState<OneStep | null>(null);
  const [oneAnswers, setOneAnswers] = useState<ChoiceAnswers>({});
  const runNow = useCallback((req: ActionRequest) => {
    setOneAnswers({});
    setOne({ req, phase: 'checking', error: null, result: null });
  }, []);

  const updateEventsOption = config.options.find((o) => o.key === UPDATE_EVENTS_OPTION) ?? null;
  const updateEvents = updateEventsOption ? optionDefault(updateEventsOption, config.optionDefaults) : null;
  const change = useMemo(() => {
    if (!one) return null;
    const path = actionDraftPath(one.req.action);
    return { rec: one.req.rec, path, from: baselineOf(one.req.rec, path), to: one.req.to };
  }, [one, baselineOf]);
  // The questions this one change raises (a cancellation's mode), answered here.
  const oneState = useMemo(
    () => (change ? { ...initialDraftState, entries: { [change.rec]: { [change.path]: { from: change.from, to: change.to } } } } : null),
    [change],
  );
  const raised = useMemo(
    () =>
      oneState
        ? raisedChoices({
            options: config.options,
            state: oneState,
            answers: oneAnswers,
            ...(config.optionDefaults ? { optionDefaults: config.optionDefaults } : {}),
            ...(config.isOptionAsked ? { isOptionAsked: config.isOptionAsked } : {}),
          })
        : [],
    [oneState, oneAnswers, config.options, config.optionDefaults, config.isOptionAsked],
  );
  // A confirmed answer here confirms the one change's value: it cannot change under it.
  const confirmers = useMemo(() => confirmersOf(config.options), [config.options]);
  const body = useMemo(() => {
    if (!change || !oneState) return null;
    const confirmed = confirmedValues(raised, confirmers, (key) => {
      const option = config.options.find((o) => o.key === key);
      return option ? bindAnswer(option, oneState) : undefined;
    });
    return oneStepChangeset(change, choiceExtras(raised, typeof updateEvents === 'string' ? updateEvents : null, [], confirmed));
  }, [change, oneState, raised, updateEvents, confirmers, config.options]);
  const previewing = one !== null && (one.phase === 'checking' || one.phase === 'confirm');
  const query = config.usePreview(previewing ? body : null);
  const plan: RecordPreview | null = previewing && query.data && !query.isPlaceholderData ? query.data : null;
  const warnings = change ? (config.changeWarnings?.(change.rec, change.path, change.to) ?? []) : [];
  const lines = plan
    ? [...(one?.req.confirm ? [one.req.confirm] : []), ...confirmItems(plan, config.confirmFollowUps), ...warnings]
    : [];
  const missing = raised.some((c) => c.option.required && c.answer === null);

  const latest = useRef({ one, mode });
  latest.current = { one, mode };
  const onSaved = useCallback(
    (result: RecordApplyResult) => {
      setReceipt({ result, count: 1, description: '' });
      // Edit mode: the ledger shows the receipt. View mode: it stays on the control.
      setOne((o) => (!o ? o : latest.current.mode === 'edit' ? null : { ...o, phase: 'done', result }));
    },
    [setReceipt],
  );
  const apply = config.useApply({ onSaved });
  const doApply = useCallback(() => {
    if (!body) return;
    setOne((o) => (o ? { ...o, phase: 'saving' } : o));
    apply.mutate(body, {
      onError: (e) => {
        const drift = asDriftFailure(e);
        const message = drift
          ? 'It changed on the server meanwhile — the page now shows the stored value. Nothing was saved.'
          : asValidationFailure(e)
            ? refusalMessage(asValidationFailure(e)!)
            : failureMessage(e);
        setOne((o) => (o ? { ...o, phase: 'failed', error: message } : o));
      },
    });
  }, [apply, body]);

  // Checking → confirm, apply at once, or failed — once the preview of THIS body is in.
  useEffect(() => {
    if (!one || one.phase !== 'checking') return;
    if (query.error && !query.isFetching) {
      const refused = asValidationFailure(query.error);
      if (refused) {
        setOne({ ...one, phase: 'failed', error: refusalMessage(refused) });
      } else {
        setOne({ ...one, phase: 'confirm' }); // the unavailable plan is named in the lines
      }
      return;
    }
    if (!plan) return;
    const conflict = plan.conflicts[0];
    if (conflict) {
      setOne({ ...one, phase: 'failed', error: conflict.detail });
      return;
    }
    if (lines.length === 0 && raised.length === 0) doApply();
    else setOne({ ...one, phase: 'confirm' });
    // `lines` / `raised` derive from `plan` and `one`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [one, plan, query.error, query.isFetching, doApply]);

  // ---- pickers ------------------------------------------------------------------
  const [widget, setWidget] = useState<{ action: RecordAction; rec: RecordKey; anchorEl: HTMLElement } | null>(null);
  const openWidget = useCallback(
    (action: RecordAction, rec: RecordKey, anchorEl: HTMLElement) => setWidget({ action, rec, anchorEl }),
    [],
  );
  const Widget = widget?.action.widget ? config.actionWidgets?.[widget.action.widget] : undefined;

  const busy = one !== null && one.phase !== 'done' && one.phase !== 'failed';
  const value = useMemo<RecordActionsValue>(
    () => ({ isAvailable, modes, valueOf, queue, runNow, openWidget, busy }),
    [isAvailable, modes, valueOf, queue, runNow, openWidget, busy],
  );

  const titleId = useId();
  const closeOne = () => setOne(null);
  const unavailable = one?.phase === 'confirm' && !plan && Boolean(query.error);
  const confirmLines = unavailable
    ? [...(one?.req.confirm ? [one.req.confirm] : []), 'The preview is unavailable, so what this save triggers could not be checked', ...warnings]
    : lines;

  return (
    <Ctx.Provider value={value}>
      {children}

      {/* A transition that asks before it is queued. */}
      <Popover
        open={pendingQueue !== null && Boolean(pendingQueue.anchorEl)}
        anchorEl={pendingQueue?.anchorEl}
        onClose={() => setPendingQueue(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { role: 'alertdialog', 'aria-labelledby': `${titleId}-queue`, sx: POPOVER_PAPER_SX } as object }}
      >
        <Typography id={`${titleId}-queue`} variant="body2">
          {pendingQueue?.confirm} — add it to the draft?
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setPendingQueue(null)}>
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            autoFocus
            onClick={() => {
              const req = pendingQueue;
              setPendingQueue(null);
              if (req) queueNow(req);
            }}
          >
            {pendingQueue?.label}
            <Kbd>↵</Kbd>
          </Button>
        </Box>
      </Popover>

      {/* "… now": checking → confirm → saving → receipt (view mode) or failed. */}
      <Popover
        open={one !== null && Boolean(one.req.anchorEl)}
        anchorEl={one?.req.anchorEl}
        onClose={() => {
          if (one?.phase !== 'saving') closeOne();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            role: one?.phase === 'done' ? 'dialog' : 'alertdialog',
            'aria-labelledby': titleId,
            'aria-busy': busy || undefined,
            'data-one-step': one?.phase,
            sx: POPOVER_PAPER_SX,
          } as object,
        }}
      >
        {one && (
          <>
            <Box>
              <Typography id={titleId} variant="body2" sx={{ fontWeight: 600 }}>
                {one.req.label} now
              </Typography>
              {one.req.subject && (
                <Typography variant="caption" color="text.secondary" component="div">
                  {one.req.subject}
                </Typography>
              )}
            </Box>
            {one.phase === 'checking' && (
              <Box sx={{ display: 'grid', gap: 0.75 }}>
                <LinearProgress aria-label="Checking what it does" sx={{ height: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  Checking what it does…
                </Typography>
              </Box>
            )}
            {(one.phase === 'confirm' || one.phase === 'saving') && (
              <>
                {raised.map((c) => (
                  <ChoiceQuestion
                    key={c.option.key}
                    choice={c}
                    onAnswer={(key, v) => setOneAnswers((a) => ({ ...a, [key]: v }))}
                  />
                ))}
                {confirmLines.length > 0 && (
                  <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.25, typography: 'body2' }}>
                    {confirmLines.map((l) => (
                      <li key={l}>{l}</li>
                    ))}
                  </Box>
                )}
                {!plan && !unavailable && <LinearProgress aria-label="Updating what it does" sx={{ height: 2 }} />}
                <Typography variant="caption" color="text.secondary">
                  Saves just this change, with the default event policy — open the editor to combine it with others.
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={closeOne} disabled={one.phase === 'saving'}>
                    Back
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    autoFocus
                    disabled={one.phase === 'saving' || missing || (!plan && !unavailable)}
                    onClick={doApply}
                    data-confirm-now
                  >
                    {one.phase === 'saving' ? 'Saving…' : one.req.label}
                    {one.phase !== 'saving' && <Kbd>↵</Kbd>}
                  </Button>
                </Box>
              </>
            )}
            {one.phase === 'failed' && (
              <>
                <Alert severity="error" sx={{ py: 0, '& .MuiAlert-message': { typography: 'caption', py: 0.75 } }}>
                  Nothing was saved: {one.error}
                </Alert>
                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                  <Button size="small" autoFocus onClick={closeOne}>
                    Close
                  </Button>
                </Box>
              </>
            )}
            {one.phase === 'done' && one.result && (
              <LedgerReceipt
                receipt={{ result: one.result, count: 1, description: '' }}
                timezone={config.timezone}
                {...(config.eventStatusLabel ? { eventStatusLabel: config.eventStatusLabel } : {})}
                deliveriesHref={config.deliveriesHref}
                onClose={closeOne}
                closeLabel="Close"
              />
            )}
          </>
        )}
      </Popover>

      {/* A picker-driven action (change owner). */}
      <Popover
        open={widget !== null && Widget !== undefined}
        anchorEl={widget?.anchorEl}
        onClose={() => setWidget(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { role: 'dialog', 'aria-label': widget?.action.label, sx: { ...POPOVER_PAPER_SX, width: 400 } } as object }}
      >
        {widget && Widget && (
          <Widget action={widget.action} rec={widget.rec} anchorEl={widget.anchorEl} onClose={() => setWidget(null)} />
        )}
      </Popover>
    </Ctx.Provider>
  );
}

export function useRecordActions(): RecordActionsValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRecordActions must be used inside <RecordEditorProvider>');
  return v;
}

export interface RecordActionMenuItem {
  key: string;
  label: string;
  /** "adds to draft" · "saves at once" · "opens the editor". */
  hint: string;
  onSelect: (anchorEl: HTMLElement | null) => void;
}

/** The header Actions menu's "Record" group: every record-changing action offered on
 *  `rec` (the opened record), with the same handlers as the panels' controls — queue in
 *  edit mode, "… now" when the draft is clean in view mode. A started "… now" or picker
 *  hangs on the Actions button; a queued action focuses its field. */
export function useRecordActionItems(entity: string, id: number): RecordActionMenuItem[] {
  const { config, mode } = useRecordEditor();
  const actions = useRecordActions();
  const rec = recordKey(entity, id);
  const items: RecordActionMenuItem[] = [];
  for (const action of config.actions) {
    if (!actions.isAvailable(action, rec)) continue;
    const m = actions.modes(action);
    if (!m.queue && !m.now) continue;
    // A control queues whenever it can; "… now" is the view-mode default on a clean draft.
    const hint = m.queue ? (mode === 'edit' ? 'adds to draft' : 'opens the editor') : 'saves at once';
    if (action.widget) {
      items.push({
        key: action.key,
        label: `${action.label}…`,
        hint,
        onSelect: (anchorEl) => anchorEl && actions.openWidget(action, rec, anchorEl),
      });
      continue;
    }
    const current = actions.valueOf(action, rec);
    for (const t of offeredTransitions(action, current)) {
      const label = action.scope === 'participant' ? `${action.label}: ${t.label}` : t.label;
      const req = (anchorEl: HTMLElement | null): ActionRequest => ({
        action,
        rec,
        to: t.to,
        label,
        anchorEl,
        confirm: t.confirm ?? null,
        focusField: true,
      });
      items.push({
        key: `${action.key}:${String(t.to)}`,
        label: t.confirm ? `${label}…` : label,
        hint,
        onSelect: (anchorEl) => (m.queue ? actions.queue(req(anchorEl)) : actions.runNow(req(anchorEl))),
      });
    }
  }
  return items;
}
